import { ExternalLink } from "lucide-react";

import type { AppEnv } from "@/lib/config";
import { config } from "@/lib/config";
import { cn } from "@/lib/utils";

const BANNERS: Partial<Record<AppEnv, { label: string; className: string }>> = {
  dev: { label: "DEV", className: "bg-blue-600" },
  staging: { label: "STAGING", className: "bg-orange-500" },
};

/** A sandbox's logs (Dozzle group: API + fronts, Sentry environment) and its pull request. */
function sandboxLinks(slug: string, pr: string | null) {
  return [
    { label: "Logs", href: `${config.SANDBOX_LOGS_URL}/group/${slug}` },
    { label: "Sentry", href: `https://upclaim.sentry.io/explore/logs/?environment=sandbox-${slug}` },
    ...(pr ? [{ label: `PR #${pr}`, href: `https://github.com/upclaim/monorepo/pull/${pr}` }] : []),
  ];
}

/**
 * Full-width environment ribbon pinned above the whole shell — so nobody mistakes
 * staging data for production. Renders nothing in production. PR sandboxes get a violet
 * ribbon with their slug and links (new tab) to their logs and pull request.
 *
 * The bar sits in normal document flow (it is the first thing under #root), but the
 * sidebar is `position: fixed`, so globals.css offsets the fixed sidebar and shrinks
 * the `svh`-sized shells whenever `[data-env-banner]` is present.
 */
export function EnvBanner(): React.ReactElement | null {
  if (config.SANDBOX_SLUG) {
    return (
      <div
        data-env-banner
        className="flex h-(--env-banner-height) w-full shrink-0 items-center justify-center gap-4 bg-violet-600 text-xs text-white"
      >
        <span className="font-semibold uppercase tracking-widest">Sandbox {config.SANDBOX_SLUG}</span>
        {sandboxLinks(config.SANDBOX_SLUG, config.SANDBOX_PR).map(({ label, href }) => (
          <a
            key={label}
            href={href}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 font-medium text-white/90 underline-offset-2 hover:text-white hover:underline"
          >
            {label}
            <ExternalLink className="size-3" />
          </a>
        ))}
      </div>
    );
  }

  const banner = BANNERS[config.APP_ENV];

  if (!banner) {
    return null;
  }

  return (
    <div
      data-env-banner
      className={cn(
        "flex h-(--env-banner-height) w-full shrink-0 items-center justify-center",
        banner.className,
      )}
    >
      <span className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white">{banner.label}</span>
    </div>
  );
}
